import { api } from '@mbscan/client-api'
import { isPresent, isSome } from '@mbscan/shared'
import { createFileRoute, getRouteApi, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { CenteredScreen } from '@/components/layout/centered-screen'
import { DetailScreen } from '@/components/screens/detail-screen'
import { deleteMetalTagScanEntry } from '@/features/scan/detail-actions'
import { parseOptionalLengthInMeters, parseOptionalQuantity } from '@/features/scan/detail-form'
import { toResultViewModel } from '@/features/scan/result-view-model'
import type { Result } from '@/features/scan/types'
import { useActiveTenant } from '@/features/tenant/tenant-context'

const routeApi = getRouteApi('/t/$tenant_id/millsheet/history/$result_id')

function DetailPage() {
  const tenant = useActiveTenant()
  const { result_id: resultId } = routeApi.useParams()
  const navigate = useNavigate()
  const client = api.useClient()
  const { data, loading, refetch } = api.useFetchMetalTagScanResultQuery({ variables: { id: resultId } })
  const [duplicateCandidates, setDuplicateCandidates] = useState<readonly Result[]>([])
  const [operationError, setOperationError] = useState<string>()
  const entry = data?.metalTagScanResult
  const duplicateIds = entry?.duplicateResultIds.join(',') ?? ''

  useEffect(() => {
    if (duplicateIds.length === 0) {
      setDuplicateCandidates([])
      return
    }
    const fetchCandidates = async () => {
      try {
        const responses = await Promise.all(
          duplicateIds.split(',').map(async (id) => await client.fetchMetalTagScanResult({ id })),
        )
        setDuplicateCandidates(
          responses.flatMap((response) => {
            const candidate = response.metalTagScanResult
            return isSome(candidate) ? [toResultViewModel(candidate)] : []
          }),
        )
      } catch {
        setOperationError('重複候補を読み込めませんでした。通信状況を確認して再度お試しください。')
      }
    }
    void fetchCandidates()
  }, [client, duplicateIds])

  // 全画面ローディングは「まだ何も出せない初回読み込み」だけに限る。
  // 保存後の refetch でも loading が立つため、単に loading を見ると DetailScreen ごと
  // アンマウントされ、画面が一瞬まっさらになったうえに画面側の状態（保存完了トースト等）が
  // すべて捨てられてしまう（「保存したのに何も表示されない」の原因）
  if (loading && !isSome(entry)) {
    return <CenteredScreen aria-label="読取結果を読み込み中" />
  }
  if (!isSome(entry)) {
    return <CenteredScreen>読取結果が見つかりませんでした。</CenteredScreen>
  }
  const result = toResultViewModel(entry)
  const navigateHistory = () => {
    void navigate({ to: '/t/$tenant_id/millsheet/history', params: { tenant_id: tenant.id } })
  }

  return (
    <DetailScreen
      key={entry.id}
      result={result}
      buildingPartOptions={tenant.buildingParts}
      duplicateCandidates={duplicateCandidates}
      operationError={operationError}
      onBack={navigateHistory}
      onSelectDuplicate={(candidate) => {
        void navigate({
          to: '/t/$tenant_id/millsheet/history/$result_id',
          params: { tenant_id: tenant.id, result_id: String(candidate.id) },
        })
      }}
      onConfirmDuplicate={async () => {
        setOperationError(undefined)
        try {
          await client.updateMillsheetScanLog({ input: { id: entry.id, reviewed: true } })
          await refetch()
        } catch (error) {
          setOperationError('重複確認を保存できませんでした。通信状況を確認して再度お試しください。')
          throw error
        }
      }}
      onDelete={async () => {
        setOperationError(undefined)
        try {
          await deleteMetalTagScanEntry({
            id: entry.id,
            kind: entry.kind,
            deleteFailure: async (id) => await client.deleteMetalTagScanFailure({ id }),
            deleteResult: async (id) => await client.deleteMetalTagScanResult({ id }),
          })
          navigateHistory()
        } catch (error) {
          setOperationError('削除できませんでした。通信状況を確認して再度お試しください。')
          throw error
        }
      }}
      onUpdate={async (updated) => {
        setOperationError(undefined)
        const quantity = parseOptionalQuantity(updated.quantity)
        if (!quantity.valid) {
          setOperationError('数量は1以上の整数で入力してください。')
          return
        }
        const length = parseOptionalLengthInMeters(updated.length)
        if (!length.valid) {
          setOperationError('長さは0より大きいm単位の数値で入力してください。')
          return
        }
        // 画面側は「未入力」を空文字で表すため、保存時は trim して null に正規化する（'' や空白のみを
        // 保存すると「値はあるが空」という状態がDBに残り、未入力との区別がつかなくなる）
        const orNull = (value: string | undefined) => {
          const trimmed = value?.trim()
          return isPresent(trimmed) ? trimmed : null
        }
        const input = {
          branchNumber: orNull(updated.branchNumber),
          buildingPart: orNull(updated.area),
          // heat_number は重複判定（scan_fingerprint_fixed）と自動紐付けのキーになるため、
          // 前後の空白が入ると別物として扱われてしまう
          heatNumber: updated.number.trim(),
          length: length.value,
          notificationNo: orNull(updated.notificationNumber),
          quantity: quantity.value,
          size: orNull(updated.size),
          steelType: orNull(updated.standard),
        }
        try {
          if (entry.kind === 'failure') {
            const response = await client.resolveMetalTagScanFailure({ input: { ...input, failureId: entry.id } })
            const resolvedId = response.data?.resolveMetalTagScanFailure.id
            if (isSome(resolvedId)) {
              await navigate({
                to: '/t/$tenant_id/millsheet/history/$result_id',
                params: { tenant_id: tenant.id, result_id: resolvedId },
                replace: true,
              })
            }
            return
          }
          await client.updateMillsheetScanLog({ input: { ...input, id: entry.id } })
          await refetch()
        } catch (error) {
          setOperationError('変更を保存できませんでした。通信状況を確認して再度お試しください。')
          throw error
        }
      }}
    />
  )
}

export const Route = createFileRoute('/t/$tenant_id/millsheet/history/$result_id')({ component: DetailPage })
